import { ParsedUrlQuery } from 'querystring';
import { snakeToCamelCase } from './common';
import { ItemFilter } from '../../graphql/types';

type IntFilterKeys = {
  [K in keyof ItemFilter]-?: NonNullable<ItemFilter[K]> extends number
    ? K
    : never;
}[keyof ItemFilter];

const INT_FILTER_FIELDS: Record<IntFilterKeys, true> = {
  areaFrom: true,
  areaTo: true,
  floorFrom: true,
  floorTo: true,
  landAreaFrom: true,
  landAreaTo: true,
  priceFrom: true,
  priceTo: true,
  roomsFrom: true,
  roomsTo: true
};

const isIntFilterKey = (key: string): key is IntFilterKeys =>
  Object.prototype.hasOwnProperty.call(INT_FILTER_FIELDS, key);

const parseInteger = (value: string | string[]): number | undefined => {
  if (typeof value !== 'string') return undefined;

  const trimmed = value.trim();
  if (!trimmed.length) return undefined;

  const parsed = Number(trimmed);

  return Number.isInteger(parsed) ? parsed : undefined;
};

export const parseVippedFiltersFromQuery = (
  query: ParsedUrlQuery
): ItemFilter => {
  if (!query || typeof query !== 'object') return {} as ItemFilter;

  const { id, nextInternalLocale, ...filterParams } = query;

  const entries = Object.entries(filterParams).reduce<[string, unknown][]>(
    (acc, [key, value]) => {
      const newKey = key.replace(/q\[([^\]]*)\]|\[\]/g, '$1');
      const camelKey = snakeToCamelCase(newKey);
      const strValue = value as string | string[];

      if (newKey === 'building_type') {
        acc.push([camelKey, value]);
      } else if (isIntFilterKey(camelKey)) {
        const parsed = parseInteger(strValue);

        if (parsed !== undefined) acc.push([camelKey, parsed]);
      } else if (Array.isArray(strValue)) {
        acc.push([camelKey, strValue]);
      } else if (newKey === 'room_ids') {
        acc.push([camelKey, [strValue]]);
      } else {
        acc.push([
          camelKey,
          !Number.isNaN(+strValue) ? strValue : strValue === 'true'
        ]);
      }

      return acc;
    },
    []
  );

  return Object.fromEntries(entries) as ItemFilter;
};
